import { useContext, useEffect, useState } from "react";
import {
  Modal,
  Tag,
  Button,
  Input,
  Space,
  Divider,
  Avatar,
  Row,
  Col,
  Alert,
  Timeline,
  Typography,
} from "antd";
import {
  UserOutlined,
  CheckCircleFilled,
  ClockCircleFilled,
  CloseCircleFilled,
  SyncOutlined,
} from "@ant-design/icons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";

const { Text } = Typography;

const TAG_COLOR = {
  Confirm:   "success",
  Pending:   "warning",
  Separated: "error",
  Extend:    "processing",
};

const TAG_LABEL = {
  Confirm:   "Confirmed",
  Pending:   "Pending",
  Separated: "Separated",
  Extend:    "Extended",
};

// ─── Info Row ────────────────────────────────────────────────────────────
function InfoRow({ label, value }) {
  return (
    <div style={{ display: "flex", marginBottom: 7, alignItems: "flex-start" }}>
      <span style={{ width: 148, color: "#888", fontSize: 12.5, flexShrink: 0, lineHeight: "1.5" }}>
        {label}:
      </span>
      <span style={{ fontWeight: 500, fontSize: 12.5, color: "#222", lineHeight: "1.5" }}>
        {value || "—"}
      </span>
    </div>
  );
}

// ─── Step Status Icon ────────────────────────────────────────────────────
function StepIcon({ status }) {
  if (status === "Confirm")   return <CheckCircleFilled  style={{ color: "#52c41a", fontSize: 15 }} />;
  if (status === "Separated") return <CloseCircleFilled  style={{ color: "#ff4d4f", fontSize: 15 }} />;
  if (status === "Extend")    return <SyncOutlined        style={{ color: "#1890ff", fontSize: 15 }} />;
  return                             <ClockCircleFilled  style={{ color: "#faad14", fontSize: 15 }} />;
}

// ─── Timeline dot color ──────────────────────────────────────────────────
function timelineDotColor(status) {
  if (status === "Confirm")   return "green";
  if (status === "Separated") return "red";
  if (status === "Extend")    return "blue";
  return "gray";
}

// ─── Component ───────────────────────────────────────────────────────────
export default function ProbationReviewModal({ visible, employee, onClose }) {
  const { authData, backUrl } = useContext(AuthContext);

  const [remarks, setRemarks]                       = useState("");
  const [newProbationDate, setNewProbationDate]     = useState(null);
  const [extendModalVisible, setExtendModalVisible] = useState(false);

  const [confirmLoading, setConfirmLoading]             = useState(false);
  const [separateLoading, setSeparateLoading]           = useState(false);
  const [submitExtendLoading, setSubmitExtendLoading]   = useState(false);

  const [activeStepData, setActiveStepData] = useState(null);
  const [canEdit, setCanEdit]               = useState(false);
  const [allSteps, setAllSteps]             = useState([]);

  const isSuperuser  = authData?.employee?.is_superuser;
  const myEmployeeId = authData?.employee?.id;

  // ─── Derive active step ──────────────────────────────────────────────
  useEffect(() => {
    if (!employee) return;

    const steps = employee.approval_steps || [];
    setAllSteps(steps);
    setRemarks("");
    setNewProbationDate(null);

    // Step assigned to the logged-in non-superuser
    const myStep = steps.find((s) => s.approver === myEmployeeId);

    // First pending step in the chain
    const currentPending = steps.find((s) => s.status === "Pending");

    let active = null;

    if (myStep) {
      // Regular approver — use their own step
      active = myStep;
    } else if (isSuperuser && currentPending) {
      // Superuser: can act on the current pending step ONLY IF
      // ALL steps BEFORE it are "Confirm" (i.e. it's genuinely their turn)
      const pendingIdx = steps.findIndex((s) => s.id === currentPending.id);
      const prevSteps  = steps.slice(0, pendingIdx);
      const allPrevConfirmed = prevSteps.every((s) => s.status === "Confirm");

      if (allPrevConfirmed) {
        active = currentPending;
      }
    }

    setActiveStepData(active);

    if (active?.status === "Pending") {
      const idx      = steps.findIndex((s) => s.id === active.id);
      const prevStep = idx > 0 ? steps[idx - 1] : null;
      // Can edit if first step OR previous step is fully Confirmed
      setCanEdit(!prevStep || prevStep.status === "Confirm");
    } else {
      setCanEdit(false);
    }

    // Pre-fill own remarks if already submitted
    if (myStep?.remarks) setRemarks(myStep.remarks);
  }, [employee, myEmployeeId, isSuperuser]);

  if (!employee) return null;
  const emp = employee.employee;

  // ─── Submit decision ─────────────────────────────────────────────────
  const handleDecision = async (actionType) => {
    if (!activeStepData) {
      toast.error("No active approval step found for your account.");
      return;
    }
    if (!remarks.trim()) {
      toast.warning("Please enter your remarks before submitting.");
      return;
    }
    if (actionType === "Extend") {
      setExtendModalVisible(true);
      return;
    }

    try {
      if (actionType === "Confirm")   setConfirmLoading(true);
      if (actionType === "Separated") setSeparateLoading(true);

      await axios.post(
        `${backUrl}/api/probation/probation-approvals/${activeStepData.id}/submit-decision/`,
        { action: actionType, remarks },
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );

      toast.success(`Decision "${actionType}" submitted successfully.`);
      onClose();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.error   ||
        err?.response?.data?.message ||
        "Something went wrong.";
      toast.error(msg);
    } finally {
      setConfirmLoading(false);
      setSeparateLoading(false);
    }
  };

  // ─── Extend submit ───────────────────────────────────────────────────
  const handleExtendSubmit = async () => {
    if (!newProbationDate) { toast.warning("Please select an extension date."); return; }
    if (!remarks.trim())   { toast.warning("Remarks are required.");             return; }
    if (!activeStepData)   { toast.error("No active approval step found.");      return; }

    try {
      setSubmitExtendLoading(true);
      await axios.post(
        `${backUrl}/api/probation/probation-approvals/${activeStepData.id}/submit-decision/`,
        {
          action:                 "Extend",
          remarks,
          new_probation_end_date: dayjs(newProbationDate).format("YYYY-MM-DD"),
        },
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );
      toast.success(`Probation extended to ${dayjs(newProbationDate).format("DD/MM/YYYY")}`);
      setExtendModalVisible(false);
      onClose();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
        err?.response?.data?.error   ||
        "Failed to extend probation."
      );
    } finally {
      setSubmitExtendLoading(false);
    }
  };

  const dueDaysColor =
    employee.due_days == null ? "default"
    : employee.due_days < 0  ? "error"
    : employee.due_days <= 7 ? "warning"
    : "success";

  const dueDaysLabel =
    employee.due_days == null
      ? "—"
      : employee.due_days < 0
        ? `${Math.abs(employee.due_days)} days overdue`
        : `${employee.due_days} days left`;

  // Flag: superuser acting on a step that's not their own
  const actingAsHR = isSuperuser && activeStepData && activeStepData.approver !== myEmployeeId;

  // ─── Timeline items for approval chain ──────────────────────────────
  const timelineItems = allSteps.map((step) => {
    const isActive  = activeStepData?.id === step.id && canEdit;
    const dotColor  = isActive ? "#0e497b" : timelineDotColor(step.status);

    return {
      dot: (
        <div
          style={{
            width:          26,
            height:         26,
            borderRadius:   "50%",
            background:     isActive ? "#0e497b" : dotColor === "gray" ? "#d9d9d9" : dotColor === "green" ? "#52c41a" : dotColor === "red" ? "#ff4d4f" : "#1890ff",
            display:        "flex",
            alignItems:     "center",
            justifyContent: "center",
            color:          "white",
            fontWeight:     700,
            fontSize:       11,
          }}
        >
          {step.order}
        </div>
      ),
      children: (
        <div
          style={{
            padding:      "10px 14px",
            borderRadius: 8,
            border:       `1px solid ${isActive ? "#0e497b" : step.status === "Confirm" ? "#b7eb8f" : step.status === "Separated" ? "#ffa39e" : step.status === "Extend" ? "#91caff" : "#ffe58f"}`,
            background:   isActive ? "#e6f0fa" : step.status === "Confirm" ? "#f6ffed" : step.status === "Separated" ? "#fff1f0" : step.status === "Extend" ? "#e6f4ff" : "#fffbe6",
            marginBottom:  4,
          }}
        >
          {/* Header row */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <StepIcon status={step.status} />
              <Text strong style={{ fontSize: 13 }}>
                {step.approver_name}
              </Text>
              {step.approver === myEmployeeId && (
                <Tag color="blue" style={{ fontSize: 10, padding: "0 5px", lineHeight: "18px" }}>You</Tag>
              )}
              {isActive && actingAsHR && (
                <Tag color="purple" style={{ fontSize: 10, padding: "0 5px", lineHeight: "18px" }}>Acting as HR</Tag>
              )}
            </div>
            <Tag color={TAG_COLOR[step.status] || "default"} style={{ margin: 0 }}>
              {TAG_LABEL[step.status] || step.status}
            </Tag>
          </div>

          {/* Remarks */}
          {step.remarks && (
            <div style={{ marginTop: 6, fontSize: 12.5, color: "#444", fontStyle: "italic" }}>
              💬 "{step.remarks}"
            </div>
          )}

          {/* Completed on */}
          {step.completed_on && (
            <div style={{ marginTop: 4, fontSize: 11.5, color: "#999" }}>
              ✅ Completed: {dayjs(step.completed_on).format("DD MMM YYYY [at] hh:mm A")}
            </div>
          )}

          {/* Extended date */}
          {step.new_probation_end_date && (
            <div style={{ marginTop: 4, fontSize: 11.5, color: "#1890ff" }}>
              📅 Extended to: {new Date(step.new_probation_end_date).toLocaleDateString("en-GB")}
            </div>
          )}
        </div>
      ),
    };
  });

  // ─── Render ──────────────────────────────────────────────────────────
  return (
    <>
      <ToastContainer position="top-right" autoClose={2500} hideProgressBar />

      {/* ── Main Modal ───────────────────────────────────────────────── */}
      <Modal
        open={visible}
        onCancel={onClose}
        footer={null}
        width={820}
        destroyOnClose
        styles={{ body: { padding: "0 24px 24px" } }}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Avatar
              src={emp?.photo}
              icon={<UserOutlined />}
              size={44}
              style={{ backgroundColor: "#0e497b", flexShrink: 0 }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, color: "#0e497b", fontSize: 15, lineHeight: 1.3 }}>
                {emp?.first_name} {emp?.last_name}
              </div>
              <div style={{ fontSize: 12, color: "#888", fontWeight: 400, marginTop: 2 }}>
                {emp?.emp_code}&nbsp;&bull;&nbsp;{emp?.designation_name}
              </div>
            </div>
            <Tag
              color={TAG_COLOR[employee.overall_status] || "default"}
              style={{ fontSize: 12, marginLeft: "auto", flexShrink: 0 }}
            >
              {TAG_LABEL[employee.overall_status] || employee.overall_status}
            </Tag>
          </div>
        }
      >
        {/* ── Employee Details ─────────────────────────────────── */}
        <div
          style={{
            background:    "#f0f5fb",
            borderRadius:  10,
            padding:       "14px 18px",
            marginBottom:  16,
            marginTop:     16,
          }}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <InfoRow label="Organisation"   value={emp?.organisation_name} />
              <InfoRow label="Department"     value={emp?.department_name} />
              <InfoRow label="Designation"    value={emp?.designation_name} />
              <InfoRow
                label="Date of Joining"
                value={emp?.date_of_joining
                  ? new Date(emp.date_of_joining).toLocaleDateString("en-GB")
                  : "—"}
              />
            </Col>
            <Col xs={24} sm={12}>
              <InfoRow label="Email"         value={emp?.email_id} />
              <InfoRow label="Mobile"        value={emp?.mobile_number} />
              <InfoRow label="Location"      value={emp?.location} />
              <InfoRow label="Reporting To"  value={emp?.reporting_to_name} />
            </Col>
          </Row>

          <Divider style={{ margin: "10px 0" }} />

          <Row gutter={16} align="middle">
            <Col xs={8}>
              <div style={{ fontSize: 11, color: "#888", marginBottom: 3 }}>Probation End Date</div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>
                {employee.final_probation_end_date
                  ? new Date(employee.final_probation_end_date).toLocaleDateString("en-GB")
                  : "—"}
              </div>
            </Col>
            <Col xs={8}>
              <div style={{ fontSize: 11, color: "#888", marginBottom: 3 }}>Due Days</div>
              <Tag color={dueDaysColor}>{dueDaysLabel}</Tag>
            </Col>
            <Col xs={8}>
              <div style={{ fontSize: 11, color: "#888", marginBottom: 3 }}>Overall Status</div>
              <Tag color={TAG_COLOR[employee.overall_status] || "default"}>
                {TAG_LABEL[employee.overall_status] || employee.overall_status}
              </Tag>
            </Col>
          </Row>
        </div>

        {/* ── Action Panel ─────────────────────────────────────── */}
        {canEdit && activeStepData && (
          <div
            style={{
              border:       "1.5px solid #0e497b",
              borderRadius: 10,
              padding:      "14px 18px",
              marginBottom: 16,
              background:   "#fff",
            }}
          >
            <div style={{ fontWeight: 600, color: "#0e497b", marginBottom: 10, fontSize: 13, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              ✏️ Your Decision — Step {activeStepData.order}: {activeStepData.approver_name}
              {actingAsHR && (
                <Tag color="purple" style={{ fontWeight: 400, fontSize: 11 }}>Acting as HR</Tag>
              )}
            </div>

            <Input.TextArea
              rows={3}
              placeholder="Enter your remarks here (required)..."
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              style={{ marginBottom: 12, borderRadius: 6 }}
            />

            <Space wrap>
              <Button
                loading={confirmLoading}
                onClick={() => handleDecision("Confirm")}
                style={{
                  backgroundColor: "#389e0d",
                  borderColor:     "#389e0d",
                  color:           "white",
                  fontWeight:      600,
                  borderRadius:    6,
                }}
              >
                ✅ Confirm
              </Button>

              <Button
                onClick={() => handleDecision("Extend")}
                style={{
                  backgroundColor: "#faad14",
                  borderColor:     "#faad14",
                  color:           "white",
                  fontWeight:      600,
                  borderRadius:    6,
                }}
              >
                🔁 Extend
              </Button>

              <Button
                danger
                loading={separateLoading}
                onClick={() => handleDecision("Separated")}
                style={{ fontWeight: 600, borderRadius: 6 }}
              >
                🚫 Separate
              </Button>
            </Space>
          </div>
        )}

        {/* Info when no action available */}
        {!canEdit && employee.overall_status === "Pending" && (
          <Alert
            message={
              activeStepData && activeStepData.status !== "Pending"
                ? "You have already submitted your decision for this review."
                : isSuperuser
                  ? "Previous approvers have not yet completed their steps."
                  : "Awaiting previous approver to take action before your turn."
            }
            type="info"
            showIcon
            style={{ marginBottom: 14, borderRadius: 8 }}
          />
        )}

        {/* ── Approval Chain ───────────────────────────────────── */}
        <div
          style={{
            fontWeight:   600,
            color:        "#0e497b",
            marginBottom: 12,
            fontSize:     13,
            display:      "flex",
            alignItems:   "center",
            gap:          8,
          }}
        >
          Approval Chain
          <Tag color="blue" style={{ fontWeight: 400, fontSize: 11 }}>
            {allSteps.length} step{allSteps.length !== 1 ? "s" : ""}
          </Tag>
        </div>

        {allSteps.length === 0 ? (
          <Alert message="No approval steps found for this review." type="warning" showIcon />
        ) : (
          <div style={{ paddingLeft: 4 }}>
            <Timeline items={timelineItems} />
          </div>
        )}
      </Modal>

      {/* ── Extend Modal ─────────────────────────────────────────── */}
      <Modal
        open={extendModalVisible}
        title="🔁 Extend Probation Period"
        onCancel={() => setExtendModalVisible(false)}
        onOk={handleExtendSubmit}
        okText="Confirm Extension"
        confirmLoading={submitExtendLoading}
        okButtonProps={{
          style:    { backgroundColor: "#0e497b", borderColor: "#0e497b" },
          disabled: !newProbationDate || !remarks.trim(),
        }}
        width={460}
      >
        <p style={{ color: "#555", marginBottom: 14 }}>Choose an extension duration:</p>

        <div style={{ display: "flex", justifyContent: "center", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
          {[30, 45, 60].map((days) => {
            const target   = dayjs().add(days, "day").format("YYYY-MM-DD");
            const selected = newProbationDate === target;
            return (
              <Button
                key={days}
                type={selected ? "primary" : "default"}
                style={selected ? { backgroundColor: "#0e497b", borderColor: "#0e497b" } : {}}
                onClick={() => setNewProbationDate(target)}
              >
                +{days} Days
              </Button>
            );
          })}
        </div>

        {newProbationDate && (
          <div
            style={{
              textAlign:    "center",
              padding:      "10px 16px",
              background:   "#f0f5fb",
              borderRadius: 8,
              marginBottom: 14,
              fontWeight:   500,
            }}
          >
            New Probation End Date:&nbsp;
            <strong style={{ color: "#0e497b" }}>
              {dayjs(newProbationDate).format("DD/MM/YYYY")}
            </strong>
          </div>
        )}

        {!remarks.trim() && (
          <Alert
            message="Please fill in your remarks in the action panel above before extending."
            type="warning"
            showIcon
            style={{ borderRadius: 8 }}
          />
        )}
      </Modal>
    </>
  );
}